      import React, {
        useCallback,
        useEffect,
        useMemo,
        useRef,
        useState,
      } from "react";

      interface Column<T> {
        key: keyof T | string;
        label: React.ReactNode; // changed: allow JSX labels (checkbox, buttons, etc.)
        render?: (item: T, index: number) => React.ReactNode;
        isHtml?: boolean;
      }

      interface DataTableProps<T> {
        data: T[];
        columns: Column<T>[];
        rowKey?: (item: T, index: number) => React.Key;
        className?: string;
      }

      /**
       * Responsive Table component
       * - Desktop: classic table with stable layout
       * - Mobile: stacked cards (up to 5 items) with dark/light support
       */
      function Table<T>({
        data,
        columns,
        rowKey = (_, index) => index,
        className = "",
      }: DataTableProps<T>): JSX.Element {
        const [isMobile, setIsMobile] = useState<boolean>(() =>
          typeof window !== "undefined"
            ? window.matchMedia("(max-width: 767.98px)").matches
            : false
        );

        const [openPopover, setOpenPopover] = useState<string | null>(null);
        const popoverAnchor = useRef<HTMLElement | null>(null);
        const styleInjected = useRef(false);

        // insert CSS once (idempotent) — includes mobile card styles and dark/light variables
        useEffect(() => {
          if (typeof document === "undefined" || styleInjected.current) return;
          const styleId = "responsive-table-styles";
          if (document.getElementById(styleId)) {
            styleInjected.current = true;
            return;
          }

          const css = `
        /* root */
        .supplier-table-root {
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          --rt-card-bg: #ffffff;
          --rt-border-color: rgba(16,24,40,0.06);
          --rt-text-color: #0f172a;
          --rt-label-color: #374151;
          --rt-value-color: #111827;
          --rt-shadow: 0 1px 0 rgba(16,24,40,0.04);
          --rt-accent: #2563eb;
          --rt-footer-color: #6b7280;
          --cta-from: #7c3aed;
          --cta-to: #06b6d4;
          --rt-thumb-bg: #fff;
          --rt-thumb-border: rgba(0,0,0,0.06);
          --tooltip-bg: #ffffff;
          --tooltip-text: #0f172a;
          --tooltip-border: rgba(16,24,40,0.08);
        }

        /* desktop table basics */
      .custom-table {
        width: 100%;
      }

      table {
        width: 100%;
        border-collapse: collapse;
      }

      th,
      td {
        vertical-align: middle;
        padding: 2px 2px;
      }

      .custom-table th:first-child,
      .custom-table td:first-child {
        text-align: center;  
      }

      .table-wrapper {
        width: 100%;
        overflow: hidden;
      }


        /* clamp & tooltip */
        .cell-wrap {
          white-space: normal !important;
          word-wrap: break-word;
          word-break: break-word;
          max-width: auto;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }

        .cell-tooltip {
          position: relative;
          cursor: pointer;
          display: inline-block;
        }

        .tooltip-hover {
          position: absolute;
          left: 50%;
          top: -8px;
          transform: translate(-50%, -6px) translateY(-6px);
          background: var(--tooltip-bg);
          color: var(--tooltip-text);
          padding: 10px 12px;
          border-radius: 12px;
          font-size: 13px;
          min-width: 160px;
          max-width: 440px;
          border: 1px solid var(--tooltip-border);
          box-shadow: 0 8px 28px rgba(2,6,23,0.08);
          opacity: 0;
          visibility: hidden;
          transition: opacity 160ms ease, transform 160ms ease, visibility 160ms;
          z-index: 60;
          pointer-events: none;
        }

        .cell-tooltip:hover .tooltip-hover {
          opacity: 1;
          transform: translate(-50%, -6px) translateY(0);
          visibility: visible;
        }

        .popover-card {
          position: absolute;
          left: 50%;
          top: 110%;
          transform: translateX(-50%);
          border-radius: 14px;
          overflow: visible;
          z-index: 85;
          min-width: 260px;
          max-width: 560px;
        }

        .popover-inner {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          padding: 18px;
          background: linear-gradient(180deg, var(--popover-bg-top,#ffffff), var(--popover-bg-bottom,#ffffff));
          border: 1px solid var(--rt-border-color);
          box-shadow: 0 18px 48px rgba(2,6,23,0.12);
        }

        .popover-badge {
          display:inline-block;
          padding:6px 10px;
          border-radius:999px;
          font-weight:700;
          font-size:12px;
          letter-spacing:0.2px;
          background: linear-gradient(90deg, rgba(99,102,241,0.12), rgba(16,185,129,0.12));
          color: var(--badge-color,#6b21a8);
          border: 1px solid rgba(99,102,241,0.12);
          margin-bottom:10px;
        }

        .popover-title { font-size:15px; font-weight:700; margin-bottom:8px; color:var(--rt-label-color); }
        .popover-desc { font-size:13px; margin-bottom:12px; color:var(--rt-value-color); line-height:1.35; }
        .popover-cta { display:flex; gap:8px; align-items:center; }
        .btn-ghost { padding:8px 14px; border-radius:8px; background:transparent; border:1px solid var(--rt-border-color); cursor:pointer; font-weight:600; }

        /* small thumbnail */
        .small-thumb {
          width:60px;
          height:60px;
          border-radius:6px;
          overflow:hidden;
          display:inline-block;
          border:1px solid var(--rt-border-color);
          background:var(--rt-card-bg);
        }
        .small-thumb img { width:100%; height:100%; object-fit:contain; display:block; }

        /* Mobile Card Styles */
        .rt-card {
          padding:12px 14px;
          border-radius:10px;
          background:var(--rt-card-bg);
          border:1px solid var(--rt-border-color);
          box-shadow:var(--rt-shadow);
          color:var(--rt-text-color);
          transition: background-color 200ms ease, color 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
        }

        .rt-card-list { display:flex; flex-direction:column; gap:12px; }
        .rt-card-row { display:flex; justify-content:space-between; gap:12px; align-items:center; margin-bottom:8px; }
        .rt-card-label { flex:0 0 40%; font-weight:700; color:var(--rt-label-color); font-size:13px; }
        .rt-card-value { flex:1 1 60%; color:var(--rt-value-color); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:13px; }

        .rt-thumb { width:48px; height:48px; border-radius:8px; overflow:hidden; display:inline-block; background:var(--rt-thumb-bg); border:1px solid var(--rt-thumb-border); }
        .rt-thumb img{ width:100%; height:100%; object-fit:cover; }

        .rt-action > * { min-width:34px; height:34px; padding:6px; border-radius:8px; margin-right:6px; display:inline-flex; align-items:center; justify-content:center; }

        .rc-factor-card { display:flex; gap:12px; align-items:center; width:100%; }
        .rc-col { min-width:0; }
        .rc-col-rc { flex:0 0 42%; }
        .rc-col-factor { flex:1 1 58%; }
        .rc-label { font-size:13px; font-weight:600; color:var(--rt-label-color); }
        .rc-value { margin-top:6px; font-size:14px; color:var(--rt-value-color); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .rc-input { margin-top:6px; width:100%; padding:6px 8px; border-radius:6px; border:1px solid var(--rt-border-color); background:transparent; color:var(--rt-value-color); }

        .group-header-row { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
        .group-header-badges { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .group-header-badges .badge { padding:6px 8px; border-radius:8px; font-weight:600; }
        .group-header-badges .part-no { background:var(--rt-accent); color:#fff; }
        .group-header-badges .principal { background:#0ea5e9; color:#fff; }
        .group-header-badges .date { background:#6b7280; color:#fff; }
        .group-header-badges .branch { background:var(--rt-accent); color:#fff; }

        .rt-footer { margin-top:8px; font-size:0.85rem; color:var(--rt-footer-color); text-align:right; }

        /* responsiveness tweaks */
        @media (max-width:420px) {
          .rt-card { padding:10px 12px; border-radius:8px; }
          .rt-card-row { margin-bottom:8px; }
          .rt-card-label, .rt-card-value { font-size:13px; }
        }

        /* dark mode defaults via prefers-color-scheme */
        @media (prefers-color-scheme: dark) {
          .supplier-table-root {
            --rt-card-bg: #071022;
            --rt-border-color: rgba(255,255,255,0.04);
            --rt-text-color: #e6eef8;
            --rt-label-color: #e6eef8;
            --rt-value-color: #cbd5e1;
            --badge-color: #a78bfa;
            --cta-from: #a78bfa;
            --cta-to: #06b6d4;
            --rt-shadow: none;
            --rt-thumb-bg: #071022;
            --rt-thumb-border: rgba(255,255,255,0.04);
            --rt-footer-color: #94a3b8;
            --tooltip-bg: #1e293b;
            --tooltip-text: #e2e8f0;
            --tooltip-border: rgba(255,255,255,0.08);
          }
        }

        /* explicit dark attribute override */
        [data-theme="dark"] .supplier-table-root {
          --rt-card-bg: #071022;
          --rt-border-color: rgba(255,255,255,0.04);
          --rt-text-color: #e6eef8;
          --rt-label-color: #e6eef8;
          --rt-value-color: #cbd5e1;
          --badge-color: #a78bfa;
          --cta-from: #a78bfa;
          --cta-to: #06b6d4;
          --rt-shadow: none;
          --rt-thumb-bg: #071022;
          --rt-thumb-border: rgba(255,255,255,0.04);
          --rt-footer-color: #94a3b8;
          --tooltip-bg: #1e293b;
          --tooltip-text: #e2e8f0;
          --tooltip-border: rgba(255,255,255,0.08);
        }
      `;

          const s = document.createElement("style");
          s.id = styleId;
          s.appendChild(document.createTextNode(css));
          document.head.appendChild(s);
          styleInjected.current = true;
        }, []);

        // responsive listener
        useEffect(() => {
          if (typeof window === "undefined") return;
          const mq = window.matchMedia("(max-width: 767.98px)");
          const handler = (ev: MediaQueryListEvent | MediaQueryList) => {
            const matches = "matches" in ev ? ev.matches : mq.matches;
            setIsMobile(matches);
          };
          handler(mq);
          if (mq.addEventListener) mq.addEventListener("change", handler as any);
          else mq.addListener(handler as any);

          return () => {
            if (mq.removeEventListener)
              mq.removeEventListener("change", handler as any);
            else mq.removeListener(handler as any);
          };
        }, []);

        // click outside to close popover
        useEffect(() => {
          const clickOutside = (ev: MouseEvent) => {
            const target = ev.target as Node | null;
            if (!target) return setOpenPopover(null);
            const pop = document.querySelector(".popover-card");
            if (pop && pop.contains(target)) return; // inside
            setOpenPopover(null);
          };
          document.addEventListener("click", clickOutside);
          return () => document.removeEventListener("click", clickOutside);
        }, []);

        const getNestedValue = useCallback(
          (obj: any, path: string): any =>
            path
              .split(".")
              .reduce(
                (acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined),
                obj
              ),
          []
        );

        const isImageUrl = useCallback((v: string) => {
          if (!v || typeof v !== "string") return false;
          const lower = v.toLowerCase();
          return (
            /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/.test(lower) ||
            lower.startsWith("http://") ||
            lower.startsWith("https://") ||
            lower.startsWith("/storage/")
          );
        }, []);

        const renderCellValue = useCallback(
          (col: Column<T>, item: T, rowIndex: number) => {
            const rawValue = col.render
              ? col.render(item, rowIndex)
              : getNestedValue(item, col.key.toString());
            if (rawValue === undefined || rawValue === null) return "";
            if (React.isValidElement(rawValue)) return rawValue;
            return rawValue;
          },
          [getNestedValue]
        );

        const isSerialLabel = useCallback(
          (label: string) =>
            /(^|\s)(sr\.?\s*no|serial\s*no|s\.?r\.?\s*no)(\s|$)/i.test(label.trim()),
          []
        );

        const MOBILE_LIMIT = 5;
        const displayedData = useMemo(
          () => (isMobile ? data.slice(0, MOBILE_LIMIT) : data),
          [data, isMobile]
        );

        // stable keys for popover cells
        const cellKey = (i: number, j: number) => `${i}-${j}`;

        // Desktop view
        const TableView = useCallback(
          () => (
            <div className={`table-responsive custom-table ${className}`}>
              <div className="table-wrapper">
                <table
                  className="table table-nowrap mb-0 supplier-table-desktop"
                  role="table"
                  aria-label="Data table"
                >
                  <thead>
                    <tr>
                      {columns.map((col, idx) => (
                        <th key={`head-${idx}`} scope="col">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedData.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center">
                          No records found
                        </td>
                      </tr>
                    ) : (
                      displayedData.map((item, i) => (
                        <tr key={rowKey(item, i)}>
                          {columns.map((col, j) => {
                            const value = renderCellValue(col, item, i);
                            const isAction = [
                              "action",
                              "read_at",
                              "image",
                              "operations",
                              "reason",
                              "operation",
                              "file",
                              "invoice_docs",
                              "payment_bill.uploaded_file",
                              "total",
                              "select",
                              "price",
                              "status",
                            ].includes(String(col?.key));

                            const isHtml = col.isHtml === true;

                            if (
                              typeof value === "string" &&
                              isImageUrl(value) &&
                              !isAction
                            ) {
                              return (
                                <td
                                  key={`cell-${i}-${j}`}
                                  style={{ maxWidth: 200 }}
                                  title={value}
                                >
                                  <div className="small-thumb">
                                    <img
                                      src={value}
                                      alt={String(
                                        getNestedValue(item, "part_no") || ""
                                      )}
                                      onError={(e) =>
                                        ((
                                          e.currentTarget as HTMLImageElement
                                        ).style.display = "none")
                                      }
                                    />
                                  </div>
                                </td>
                              );
                            }

                            const sk = cellKey(i, j);
                            const stringValue =
                              typeof value === "string" ? value : String(value ?? "");

                            return (
                              <td
                                key={`cell-${i}-${j}`}
                                style={isAction ? undefined : { maxWidth: 280 }}
                              >
                                {isAction ? (
                                  (value as React.ReactNode)
                                ) : isHtml && typeof value === "string" ? (
                                  <div
                                    className="cell-tooltip"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenPopover((p) => (p === sk ? null : sk));
                                      popoverAnchor.current =
                                        e.currentTarget as HTMLElement;
                                    }}
                                  >
                                    <div
                                      className="cell-wrap"
                                      dangerouslySetInnerHTML={{ __html: value }}
                                    />
                                    <div className="tooltip-hover" role="tooltip">
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div style={{ flex: 1, paddingLeft: 8 }}>
                                          <div
                                            style={{
                                              fontWeight: 700,
                                              marginBottom: 6,
                                              color: "var(--rt-label-color,#374151)",
                                            }}
                                          >
                                            {col.label}
                                          </div>
                                          <div
                                            style={{
                                              color: "var(--rt-value-color,#111827)",
                                              whiteSpace: "normal",
                                            }}
                                          >
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: value,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {openPopover === sk && (
                                      <div
                                        className="popover-card"
                                        role="dialog"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="popover-inner">
                                          <div className="popover-badge">DETAILS</div>
                                          <div className="popover-title">
                                            {col.label}
                                          </div>
                                          <div className="popover-desc">
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: value,
                                              }}
                                            />
                                          </div>
                                          <div className="popover-cta">
                                            <button
                                              className="btn-ghost"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenPopover(null);
                                              }}
                                            >
                                              Close
                                            </button>
                                            <button
                                              className="btn-primary"
                                              onClick={(e) => {
                                                e.stopPropagation(); /* custom action */
                                              }}
                                            >
                                              Take Action
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : col.key === "password" ? (
                                  "********"
                                ) : (
                                  <div
                                    className="cell-tooltip"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenPopover((p) => (p === sk ? null : sk));
                                      popoverAnchor.current =
                                        e.currentTarget as HTMLElement;
                                    }}
                                  >
                                    <div className="cell-wrap">
                                      {(value as React.ReactNode) ?? ""}
                                    </div>
                                    <div className="tooltip-hover" role="tooltip">
                                      <div style={{ paddingLeft: 8 }}>
                                        <div
                                          style={{
                                            fontWeight: 700,
                                            marginBottom: 6,
                                            color: "var(--rt-label-color,#374151)",
                                          }}
                                        >
                                          {col.label}
                                        </div>
                                        <div
                                          style={{
                                            color: "var(--rt-value-color,#111827)",
                                            whiteSpace: "normal",
                                          }}
                                        >
                                          {stringValue}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ),
          [
            columns,
            displayedData,
            rowKey,
            renderCellValue,
            isImageUrl,
            getNestedValue,
            openPopover,
            className,
          ]
        );

        // Mobile card view
        const CardView = useCallback(
          () => (
            <>
              <div className="rt-card-list" role="list">
                {displayedData.length === 0 ? (
                  <div className="rt-card" role="listitem">
                    <div className="rt-card-row">
                      <div className="rt-card-value text-center">
                        No records found
                      </div>
                    </div>
                  </div>
                ) : (
                  displayedData.map((item, i) => {
                    const headerKeys: string[] = [];
                    const imgCol = columns.find((c) => {
                      const v = renderCellValue(c, item, i);
                      return typeof v === "string" && isImageUrl(v as string);
                    });
                    if (imgCol) headerKeys.push(imgCol.key.toString());

                    const nonActionCols = columns.filter(
                      (c) =>
                        !String(c.label).toLowerCase().includes("action") &&
                        (!imgCol || c.key.toString() !== imgCol.key.toString()) &&
                        !isSerialLabel(String(c.label))
                    );
                    for (
                      let k = 0;
                      k < nonActionCols.length && headerKeys.length < 2;
                      k++
                    )
                      headerKeys.push(nonActionCols[k].key.toString());

                    const actionCols = columns.filter((c) =>
                      String(c.label).toLowerCase().includes("action")
                    );
                    const actionNodes = actionCols
                      .map((ac) => renderCellValue(ac, item, i))
                      .filter((n) => n !== undefined && n !== null);

                    return (
                      <div className="rt-card" key={rowKey(item, i)} role="listitem">
                        <div className="rt-card-inner">
                          {nonActionCols.slice(0, 2).map((col, idx) => {
                            const value = renderCellValue(col, item, i);
                            if (
                              typeof value === "string" &&
                              isImageUrl(value as string)
                            )
                              return null;
                            return (
                              <div className="rt-card-row" key={`mobile-top-${idx}`}>
                                <div className="rt-card-label">{col.label}</div>
                                <div
                                  className="rt-card-value"
                                  title={
                                    typeof value === "string" ? value : undefined
                                  }
                                >
                                  {col.isHtml && typeof value === "string" ? (
                                    <span
                                      dangerouslySetInnerHTML={{ __html: value }}
                                    />
                                  ) : (
                                    (value as React.ReactNode)
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            {columns.map((col, colIndex) => {
                              const keyStr = col.key.toString();
                              if (String(col.label).toLowerCase().includes("action"))
                                return null;
                              if (headerKeys.includes(keyStr)) {
                                if (headerKeys.indexOf(keyStr) < 2) return null;
                              }
                              if (isSerialLabel(String(col.label))) return null;

                              const value = renderCellValue(col, item, i);
                              if (
                                typeof value === "string" &&
                                isImageUrl(value as string)
                              )
                                return null;
                              if (
                                value === "" ||
                                value === undefined ||
                                value === null
                              )
                                return null;

                              const content =
                                col.isHtml && typeof value === "string" ? (
                                  <span dangerouslySetInnerHTML={{ __html: value }} />
                                ) : col.key === "password" ? (
                                  "********"
                                ) : (
                                  (value as React.ReactNode)
                                );

                              return (
                                <div
                                  className="rt-card-row"
                                  key={`mobile-${i}-${colIndex}`}
                                >
                                  <div className="rt-card-label">{col.label}</div>
                                  <div
                                    className="rt-card-value"
                                    title={
                                      typeof value === "string" ? value : undefined
                                    }
                                  >
                                    {content}
                                  </div>
                                </div>
                              );
                            })}

                            {actionNodes.length > 0 && (
                              <div
                                className="rt-card-row"
                                key={`mobile-${i}-actions`}
                              >
                                <div className="rt-card-label">Actions</div>
                                <div
                                  className="rt-card-value"
                                  style={{ display: "flex" }}
                                >
                                  <div className="rt-action">
                                    {actionNodes.map((node, ndx) => (
                                      <React.Fragment key={`act-node-${ndx}`}>
                                        {React.isValidElement(node) ? node : node}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {isMobile && data.length > MOBILE_LIMIT && (
                <div className="rt-footer" aria-live="polite">
                  Showing {MOBILE_LIMIT} of {data.length}
                </div>
              )}
            </>
          ),
          [
            columns,
            displayedData,
            rowKey,
            isImageUrl,
            isSerialLabel,
            renderCellValue,
            data,
          ]
        );

        return (
          <div className="supplier-table-root">
            {isMobile ? <CardView /> : <TableView />}
          </div>
        );
      }

      export default Table;
